import { IconButton, SkeletonSpinnerCentered } from '@civilgrid/ui/components';
import { useParams } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Document, Page } from 'react-pdf';
import '~/common/pdfWorker';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import type { ProjectFile } from './common';

interface Props {
  file: ProjectFile;
}

export default function PDFViewer({ file }: Props) {
  const [totalPages, setTotalPages] = useState(1);
  const [pageNumber, setPageNumber] = useState(1);
  const { projectId } = useParams({ strict: false }) as { projectId: string };

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    setPageNumber(1);

    // only fire the tracking when this first mounts, one mount = one view.
    capturePosthogEvent(POSTHOG_EVENTS.projectFileViewed, {
      project_id: projectId,
      filetype: 'pdf',
    });
  }, []);

  return (
    <div className="relative flex max-h-[90vh] max-w-[90vw] flex-col items-center overflow-auto">
      <div className="z-50 flex items-center gap-2">
        <IconButton
          icon="arrowLeft"
          variant="hollow"
          aria-label="Previous page"
          data-testid="project-pdf-prev-page"
          onClick={() => {
            if (pageNumber === 1) return;
            setPageNumber((prev) => prev - 1);
          }}
        />
        <div>
          {pageNumber} / {totalPages}
        </div>
        <IconButton
          icon="arrowRight"
          variant="hollow"
          aria-label="Next page"
          data-testid="project-pdf-next-page"
          onClick={() => {
            if (pageNumber === totalPages) return;
            setPageNumber((prev) => prev + 1);
          }}
        />
      </div>
      <div className="max-h-[70%] min-h-[70%] min-w-full max-w-full overflow-auto">
        <Document
          file={file.path}
          onLoadSuccess={(pdf) => {
            setTotalPages(pdf.numPages);
            setPageNumber(1);
          }}
          loading={<SkeletonSpinnerCentered />}
        >
          <Page pageNumber={pageNumber} renderTextLayer={false} loading={<SkeletonSpinnerCentered />} />
        </Document>
      </div>
    </div>
  );
}
